<script lang="ts">
  import CardShell from '../CardShell.svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import { SUBMIT_CHORD_LABEL } from '../keyboard';
  import type { GradeResult, PredictOutputCard } from '../types';
  import {
    FIXED_CHOICES,
    gradePredictOutput,
    isMultiline,
    type PredictAnswer,
  } from './predict-output';

  interface Props {
    card: PredictOutputCard;
    index: number;
    total: number;
    box?: number;
    /** 採点済みなら結果と回答（前のカードに戻ったとき） */
    result?: GradeResult | undefined;
    answer?: PredictAnswer | undefined;
    showUseCase?: boolean;
    /** CardShell の globalKeys（1 画面 1 カードの画面だけ true） */
    globalKeys?: boolean;
    ongrade?: ((result: GradeResult, answer: PredictAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
    /** ヒントを開いたとき（level は開いた段階、1 始まり） */
    onhint?: ((level: number) => void) | undefined;
  }

  let {
    card,
    index,
    total,
    box = 0,
    result,
    answer: savedAnswer,
    showUseCase = false,
    globalKeys = false,
    ongrade,
    onnext,
    onprev,
    onabort,
    onhint,
  }: Props = $props();

  const uid = $props.id();

  /** 選択肢: YAML の choices + コンパイルエラー + 実行時エラー */
  interface Option {
    key: string;
    label: string;
    answer: PredictAnswer;
  }

  const hasChoices = $derived((card.choices?.length ?? 0) > 0);
  const options = $derived<Option[]>([
    ...(card.choices ?? []).map((text, i): Option => ({
      key: `c${i}`,
      label: text.replace(/\n$/, ''),
      answer: { kind: 'output', text },
    })),
    ...FIXED_CHOICES.map((c): Option => ({
      key: c.kind,
      label: c.label,
      answer: { kind: c.kind },
    })),
  ]);
  const multiline = $derived(isMultiline(card));

  // 入力の状態。カードが替わったら（または保存済みの回答があれば）作り直す
  let selectedKey = $state<string | null>(null);
  let freeText = $state('');
  let lastKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key === lastKey) return;
    lastKey = key;
    freeText = '';
    selectedKey = null;
    if (savedAnswer) {
      if (savedAnswer.kind === 'output') {
        const found = options.find(
          (o) => o.answer.kind === 'output' && o.answer.text === savedAnswer.text,
        );
        if (hasChoices && found) selectedKey = found.key;
        else {
          selectedKey = 'free';
          freeText = savedAnswer.text;
        }
      } else selectedKey = savedAnswer.kind;
    }
  });

  const currentAnswer = $derived.by<PredictAnswer | null>(() => {
    if (selectedKey === 'free') return { kind: 'output', text: freeText };
    return options.find((o) => o.key === selectedKey)?.answer ?? null;
  });
  const graded = $derived(result !== undefined);
  const gradeState = $derived(graded ? 'graded' : currentAnswer ? 'answering' : 'idle');
  const canSubmit = $derived(
    currentAnswer !== null &&
      (currentAnswer.kind !== 'output' || hasChoices || currentAnswer.text.trim() !== ''),
  );

  function submit() {
    if (!currentAnswer || graded || !canSubmit) return;
    ongrade?.(gradePredictOutput(card, currentAnswer), currentAnswer);
  }

  let fieldset: HTMLFieldSetElement | undefined = $state();

  function onshortcut(e: KeyboardEvent): boolean {
    if (graded || e.shiftKey) return false;
    if (!/^[1-9]$/.test(e.key)) return false;
    const i = Number(e.key) - 1;
    const keys = hasChoices
      ? options.map((o) => o.key)
      : ['free', ...FIXED_CHOICES.map((c) => c.kind)];
    const key = keys[i];
    if (key === undefined) return false;
    selectedKey = key;
    // 選んだ要素にフォーカスを移す（design-system.md §8）
    fieldset?.querySelector<HTMLInputElement>(`input[value="${key}"]`)?.focus();
    return true;
  }

  function onFreeKeydown(e: KeyboardEvent) {
    if (!multiline && e.key === 'Enter' && !e.isComposing) {
      e.preventDefault();
      submit();
    }
  }

  const submitKeys = $derived(
    !hasChoices && multiline && selectedKey === 'free' ? [SUBMIT_CHORD_LABEL] : ['Enter'],
  );
  const isCorrectOption = (o: Option) => gradePredictOutput(card, o.answer).verdict === 'AC';
</script>

<CardShell
  {globalKeys}
  {card}
  {index}
  {total}
  state={gradeState}
  {result}
  {box}
  prompt="このコードは何を出力しますか。"
  {showUseCase}
  {submitKeys}
  {canSubmit}
  {onshortcut}
  onsubmit={submit}
  {onnext}
  {onprev}
  {onabort}
  {onhint}
>
  <CodeBlock code={card.code} />

  {#if card.stdin}
    <div class="stdin">
      <h2 class="sub">標準入力</h2>
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <pre class="output" tabindex="0" aria-label="標準入力">{card.stdin.replace(/\n$/, '')}</pre>
    </div>
  {/if}

  <fieldset bind:this={fieldset} class="answer" disabled={graded} data-testid="answer">
    <legend>出力</legend>
    {#if hasChoices}
      <ol class="choices" role="list">
        {#each options as o, i (o.key)}
          <li>
            <label
              class="choice"
              class:selected={selectedKey === o.key}
              class:correct={graded && isCorrectOption(o)}
              class:chosen-wrong={graded && selectedKey === o.key && result?.verdict !== 'AC'}
            >
              <input type="radio" name="{uid}-choice" value={o.key} bind:group={selectedKey} />
              <kbd aria-hidden="true">{i + 1}</kbd>
              {#if o.answer.kind === 'output'}
                <span class="choice-output">{o.label}</span>
              {:else}
                <span class="choice-fixed">{o.label}</span>
              {/if}
              {#if graded && isCorrectOption(o)}<span class="mark">正解</span>{/if}
            </label>
          </li>
        {/each}
      </ol>
    {:else}
      <div class="free">
        <label class="choice free-choice" class:selected={selectedKey === 'free'}>
          <input type="radio" name="{uid}-choice" value="free" bind:group={selectedKey} />
          <kbd aria-hidden="true">1</kbd>
          <span>出力を書く{multiline ? '（複数行）' : ''}</span>
        </label>
        {#if multiline}
          <textarea
            class="free-input"
            rows="3"
            spellcheck="false"
            autocapitalize="off"
            autocomplete="off"
            aria-label="出力"
            placeholder="1 行目&#10;2 行目"
            data-testid="answer-input"
            bind:value={freeText}
            readonly={graded}
            onfocus={() => (selectedKey = 'free')}
            oninput={() => (selectedKey = 'free')}></textarea>
        {:else}
          <input
            type="text"
            class="free-input"
            spellcheck="false"
            autocapitalize="off"
            autocomplete="off"
            aria-label="出力"
            data-testid="answer-input"
            bind:value={freeText}
            readonly={graded}
            onfocus={() => (selectedKey = 'free')}
            oninput={() => (selectedKey = 'free')}
            onkeydown={onFreeKeydown}
          />
        {/if}
        <ol class="choices" role="list">
          {#each FIXED_CHOICES as c, i (c.kind)}
            <li>
              <label class="choice" class:selected={selectedKey === c.kind}>
                <input type="radio" name="{uid}-choice" value={c.kind} bind:group={selectedKey} />
                <kbd aria-hidden="true">{i + 2}</kbd>
                <span class="choice-fixed">{c.label}</span>
              </label>
            </li>
          {/each}
        </ol>
      </div>
    {/if}
  </fieldset>

  {#snippet feedback()}
    {#if result?.verdict === 'WA' && result.expected !== undefined}
      {#if result.actual !== undefined}
        <OutputPanel mode="diff" expected={result.expected} actual={result.actual} />
      {:else}
        <OutputPanel mode="plain" stdout={result.expected} />
      {/if}
    {/if}
  {/snippet}
</CardShell>

<style>
  .sub {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .answer {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
  }
  .answer legend {
    font-size: var(--fs-sm);
    font-weight: var(--fw-medium);
    margin-bottom: var(--space-2);
    padding: 0;
  }
  .choices {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin: 0;
  }
  .choice {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: var(--tap-min);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface);
    font-weight: var(--fw-regular);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease-out);
  }
  .choice:hover {
    background: var(--canvas);
  }
  .choice.selected {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .choice.correct {
    border-color: var(--ok-text);
    box-shadow: 0 0 0 1px var(--ok-text);
  }
  .choice.chosen-wrong {
    border-style: dashed;
  }
  .choice:has(input:focus-visible) {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: var(--focus-offset);
  }
  .choice input:focus-visible {
    outline: none;
  }
  .choice kbd {
    flex: none;
  }
  .choice-output {
    flex: 1;
    min-width: 0;
    font-family: var(--font-mono);
    font-size: var(--fs-code);
    line-height: var(--lh-code);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .choice-fixed {
    flex: 1;
  }
  .mark {
    flex: none;
    font-size: var(--fs-sm);
    font-weight: var(--fw-bold);
    color: var(--ok-text);
  }
  .free {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .free-input {
    font-family: var(--font-mono);
    font-size: var(--fs-code);
  }
  fieldset:disabled .choice {
    cursor: default;
  }
  fieldset:disabled .choice:hover {
    background: var(--surface);
  }
</style>
